import { InstituteService } from './../../services/institute/institute.service';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ImageModule } from 'primeng/image';
import { Institute } from '../../models/institute/randomInstituteResponse';
import { ButtonModule } from 'primeng/button';
import { RouterLink, RouterModule } from '@angular/router';
import { Currency, getCurrencyDescription } from '../../models/enums/UserRole';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { InstituteCoursesV2Response } from '../../models/institute/instituteCoursesV2Response';
import { CourseLevelEnum, CourseLevelEnumDescriptions, getCourseLevelEnumFromDescription } from '../../models/enums/courseLevelEnum';
import { CurrencyService } from '../../services/currency.service';
import { CalendarModule } from 'primeng/calendar';

@Component({
  selector: 'app-institute-search-item',
  standalone: true,
  imports: [
    ImageModule, CommonModule, ButtonModule,
    RouterLink, RouterModule,
    DialogModule, DropdownModule, FormsModule, CalendarModule
  ],
  templateUrl: './institute-search-item.component.html',
  styleUrl: './institute-search-item.component.scss'
})
export class InstituteSearchItemComponent implements OnInit {
  @Input() item: Institute | undefined;
  @Input() portal: string | null = null;
  @Input() index?: number;
  private _loading: boolean = false;
  @Input() set loading(val: boolean) {
    const wasLoading = this._loading;
    this._loading = val;
    if (wasLoading && !val && this.showCourseDialog) {
      this.showCourseDialog = false;
    }
  }
  get loading(): boolean {
    return this._loading;
  }
  @Input() isInstituteSaved: boolean = false;
  @Input() isDirectApplied: boolean = false;
  @Input() isCourseView: boolean = false;

  @Input() programmeHeading: string | null = null;

  @Output() myInstituteEmitter = new EventEmitter<{
    instituteId: string,
    courseId: number | undefined,
    inTakeYear?: string,
    courseLevel?: number,
    mentorDeadline?: string | null
  }>();

  @Output() directApplyEmitter = new EventEmitter<{
    instituteId: string,
    courseId: number | undefined,
    inTakeYear?: string,
    courseLevel?: number,
    mentorDeadline?: string | null
  }>();

  actionType: 'save' | 'directApply' = 'save';

  showCourseDialog: boolean = false;
  courses: InstituteCoursesV2Response[] = [];
  selectedCourse: InstituteCoursesV2Response | undefined;
  loadingCourses: boolean = false;

  selectedIntakeYear: string | undefined;
  selectedCourseLevel: number | undefined;
  selectedDeadline: Date | null = null;
  maxDate: Date | null = null;
  minDate: Date = new Date();
  intakeYears: { label: string, value: string }[] = [];
  courseLevelOptions: { label: string, value: number }[] = [];
  hasAssignedMentor: boolean = false;

  constructor(private instituteService: InstituteService, public currencyService: CurrencyService) { }

  ngOnInit() {
    const raw = localStorage.getItem('account');
    if (raw) {
      try {
        const account = JSON.parse(raw);
        this.hasAssignedMentor = !!account?.hasAssignedMentor;
      } catch (e) {
        console.warn('Error parsing account from localStorage', e);
      }
    }
  }

  isValidDate(dateStr: string | null | undefined): boolean {
    if (!dateStr) return false;
    if (/^\d+$/.test(dateStr)) return false;
    const d = new Date(dateStr);
    return !isNaN(d.getTime());
  }

  onSaveClick() {
    this.actionType = 'save';
    this.openCourseDialog();
  }

  onDirectApplyClick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.actionType = 'directApply';
    this.openCourseDialog();
  }

  private openCourseDialog() {
    if (!this.item?.id) return;
    this.showCourseDialog = true;
    this.loadingCourses = true;
    this.selectedCourse = undefined;
    this.selectedIntakeYear = undefined;
    this.selectedCourseLevel = undefined;
    this.selectedDeadline = null;
    this.courses = [];
    this.intakeYears = this.generateIntakeYears();
    this.courseLevelOptions = this.generateCourseLevels();
    this.calculateMaxDate();

    this.instituteService.listInstituteCoursesEndpoint({ instituteId: this.item.id }).subscribe({
      next: (response) => {
        this.courses = response.result ?? [];
        if (this.isCourseView && this.item?.courseId) {
          const matchedCourse = this.courses.find(c => c.id === this.item!.courseId);
          if (matchedCourse) {
            this.selectedCourse = matchedCourse;
            if (this.item.courseLevel !== null && this.item.courseLevel !== undefined) {
              this.selectedCourseLevel = this.item.courseLevel as number;
            } else {
              this.selectedCourseLevel = getCourseLevelEnumFromDescription(matchedCourse.courseLevel as string);
            }
          }
        }
        this.loadingCourses = false;
      },
      error: () => { this.loadingCourses = false; }
    });
  }

  confirmSave() {
    if (!this.selectedCourse || !this.item?.id) return;
    const date = this.selectedDeadline;
    const isoDate = date ? `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}` : null;
    const payload = {
      instituteId: this.item.id,
      courseId: this.selectedCourse.id,
      inTakeYear: this.selectedIntakeYear,
      courseLevel: this.selectedCourseLevel,
      mentorDeadline: isoDate
    };

    if (this.actionType === 'directApply') {
      this.directApplyEmitter.emit(payload);
    } else {
      this.myInstituteEmitter.emit(payload);
    }
  }

  private calculateMaxDate() {
    this.maxDate = null;
    this.minDate = new Date();
    this.minDate.setHours(0, 0, 0, 0);

    if (this.item?.applicationDeadlineRegularDecision) {
      const deadlineStr = this.item.applicationDeadlineRegularDecision;
      let target = new Date(deadlineStr);
      let yearAppended = false;

      if (isNaN(target.getTime()) || target.getFullYear() < 2000) {
        const currentYear = new Date().getFullYear();
        target = new Date(`${deadlineStr}, ${currentYear}`);
        yearAppended = true;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (yearAppended && !isNaN(target.getTime()) && target < today) {
        const shiftedDate = new Date(target);
        shiftedDate.setFullYear(today.getFullYear() + 1);
        target = shiftedDate;
      }

      if (!isNaN(target.getTime())) {
        this.maxDate = target;
        if (this.maxDate < today) {
          this.minDate = new Date(this.maxDate);
          this.minDate.setFullYear(this.maxDate.getFullYear() - 1);
        }
        return;
      }
    }
  }

  private generateIntakeYears(): { label: string, value: string }[] {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    const maxYear = currentYear + 4;
    const years: { label: string, value: string }[] = [];

    for (let year = currentYear; year <= maxYear; year++) {
      const springDeadlinePassed = (currentYear > year - 1) ||
        (currentYear === year - 1 && currentMonth > 10);
      if (!springDeadlinePassed) {
        years.push({ label: `Spring ${year}`, value: `Spring ${year}` });
      }


      const fallDeadlinePassed = (currentYear > year) ||
        (currentYear === year && currentMonth > 3);
      if (!fallDeadlinePassed) {
        years.push({ label: `Fall ${year}`, value: `Fall ${year}` });
      }
    }
    return years;
  }

  get filteredCourses(): InstituteCoursesV2Response[] {
    if (this.selectedCourseLevel === undefined || this.selectedCourseLevel === null) return this.courses;
    const description = CourseLevelEnumDescriptions[this.selectedCourseLevel as CourseLevelEnum];
    return this.courses.filter(course => (course.courseLevel ?? '').trim() === description);
  }

  onCourseLevelChange() { this.selectedCourse = undefined; }

  onCourseSelectionChange() {
    if (this.selectedCourse && (this.selectedCourseLevel === null || this.selectedCourseLevel === undefined)) {
      this.selectedCourseLevel = getCourseLevelEnumFromDescription(this.selectedCourse.courseLevel as string);
    }
  }

  private generateCourseLevels() {
    return (Object.keys(CourseLevelEnumDescriptions) as unknown as (keyof typeof CourseLevelEnumDescriptions)[])
      .filter(key => !isNaN(Number(key)))
      .map(key => ({
        label: CourseLevelEnumDescriptions[Number(key) as CourseLevelEnum],
        value: Number(key)
      }));
  }

  cancelDialog() {
    this.showCourseDialog = false;
    this.selectedCourse = undefined;
    this.selectedCourseLevel = undefined;
    this.selectedDeadline = null;
  }

  hasFees(item: Institute | undefined): boolean {
    if (!item?.agentApplicationFees) return false;
    const fees = parseFloat(item.agentApplicationFees);
    return !isNaN(fees) && fees > 0;
  }

  getInitials(name: string): string {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return parts.length === 1 ? parts[0][0].toUpperCase() : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
private parseCurrency(value: any): Currency {
  if (value === null || value === undefined || value === '') return Currency.Dollar;

  const asNumber = Number(value);
  if (!isNaN(asNumber) && Currency[asNumber] !== undefined) {
    return asNumber as Currency;                     
  }

  if (typeof value === 'string') {
    if (Currency[value as keyof typeof Currency] !== undefined) {
      return Currency[value as keyof typeof Currency] as unknown as Currency;  
    }
    const match = [Currency.Dollar, Currency.Rupee, Currency.Euro, Currency.Pound]
      .find(c => getCurrencyDescription(c).toLowerCase() === value.trim().toLowerCase()); 
    if (match !== undefined) return match;
  }
  return Currency.Dollar;
}
getAmount(currency: any, amount: any): string {
  const amountNum = Number(amount);
  if (isNaN(amountNum) || amountNum === 0) return 'N/A';

  const currencyEnum = this.parseCurrency(currency);
  return `${getCurrencyDescription(currencyEnum)} ${amountNum.toLocaleString('en-US')}`;
}
  // getAmount(currency: any, amount: any): string {
  //   const amountNum = Number(amount);
  //   if (isNaN(amountNum) || amountNum === 0) return 'N/A';

  //   let currencyEnum: Currency = Currency.Dollar;
  //   const currencyIndex = Number(currency);
  //   if (!isNaN(currencyIndex) && Currency[currencyIndex] !== undefined) {
  //     currencyEnum = currencyIndex;
  //   } else if (typeof currency === 'string' && Currency[currency as keyof typeof Currency] !== undefined) {
  //     currencyEnum = Currency[currency as keyof typeof Currency] as unknown as Currency;
  //   }

  //   const currentDisplayCurrency = this.currencyService.getCurrency();
  //   const convertedAmount = this.currencyService.convertAmount(amountNum, currencyEnum, currentDisplayCurrency);

  //   return `${getCurrencyDescription(currentDisplayCurrency)} ${Math.round(convertedAmount).toLocaleString()}`;
  // }
}